<template>
  <div class="card">
    <div class="row">
      <h2>Incoming</h2>
      <div class="grow"></div>
      <button class="btn" :disabled="posting" @click="save">
        {{ posting ? 'Saving…' : 'Save' }}
      </button>
    </div>

    <div v-if="err" class="err">{{ err }}</div>
    <div v-if="ok" class="ok">{{ ok }}</div>

    <div class="grid">
      <label>
        <div class="lbl">Bill No</div>
        <input class="input" v-model="billNo" />
      </label>

      <label>
        <div class="lbl">Bill Date</div>
        <input class="input" type="date" v-model="billDate" />
      </label>

      <label>
        <div class="lbl">Vendor</div>
        <select class="input" v-model="vendorId">
          <option value="">Select Vendor</option>
          <option v-for="v in vendors" :key="v.id" :value="String(v.id)">{{ v.name }}</option>
        </select>
      </label>

      <label>
        <div class="lbl">Inspector (optional)</div>
        <select class="input" v-model="inspectorId">
          <option value="">None</option>
          <option v-for="i in inspectors" :key="i.id" :value="String(i.id)">{{ i.name }}</option>
        </select>
      </label>

      <label>
        <div class="lbl">Quality</div>
        <select class="input" v-model="quality">
          <option value="good">good</option>
          <option value="normal">normal</option>
          <option value="bad">bad</option>
        </select>
      </label>

      <label class="span2">
        <div class="lbl">Note (optional)</div>
        <input class="input" v-model="note" />
      </label>
    </div>

    <div class="row" style="margin-top:14px;">
      <h3>Lines</h3>
      <div class="grow"></div>
      <button class="btn secondary" @click="addLine">Add Line</button>
    </div>

    <div class="tableWrap">
      <table class="table">
        <thead>
          <tr>
            <th style="width:320px;">Material</th>
            <th style="width:120px;">Unit</th>
            <th style="width:140px; text-align:right;">Qty</th>
            <th style="width:160px; text-align:right;">Rate / Unit</th>
            <th style="width:60px;"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(ln, idx) in lines" :key="ln._k">
            <td>
              <select class="input" v-model="ln.materialSku" @change="syncUnit(idx)">
                <option value="">Select material</option>
                <option v-for="m in materials" :key="m.sku" :value="m.sku">
                  {{ m.sku }} — {{ m.name }}
                </option>
              </select>
            </td>
            <td><input class="input" v-model="ln.unit" /></td>
            <td><input class="input" v-model="ln.qty" type="number" step="0.01" style="text-align:right;" /></td>
            <td><input class="input" v-model="ln.ratePerUnit" type="number" step="0.01" style="text-align:right;" /></td>
            <td><button class="x" @click="removeLine(idx)">✕</button></td>
          </tr>
          <tr v-if="lines.length === 0">
            <td colspan="5" class="muted">No lines. Click “Add Line”.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="helper" style="margin-top:10px;">
      Backend will update stock + create IN ledger txns automatically.
    </div>
  </div>
</template>

<script>
export default {
  name: 'RmIncoming',
  data () {
    return {
      billNo: '',
      billDate: '',
      vendorId: '',
      inspectorId: '',
      quality: 'good',
      note: '',
      lines: [],
      ok: ''
    }
  },
  computed: {
    materials () { return this.$store.getters['inventory/materials'] },
    vendors () { return this.$store.getters['inventory/vendors'] },
    inspectors () { return this.$store.getters['inventory/inspectors'] },
    posting () { return !!this.$store.state.inventory.posting },
    err () { return this.$store.state.inventory.lastError }
  },
  methods: {
    addLine () {
      this.lines.push({ _k: Math.random().toString(36).slice(2), materialSku: '', unit: '', qty: '', ratePerUnit: '' })
    },
    removeLine (i) { this.lines.splice(i, 1) },
    syncUnit (i) {
      const sku = this.lines[i] && this.lines[i].materialSku
      if (!sku) return
      const m = (this.materials || []).find(x => x.sku === sku)
      if (m && !this.lines[i].unit) this.lines[i].unit = m.unit || ''
    },
    n (v) {
      if (v === '' || v === null || v === undefined) return null
      const x = Number(v)
      return Number.isFinite(x) ? x : null
    },
    async save () {
      this.ok = ''
      if (!this.billNo.trim() || !this.billDate || !this.vendorId) {
        this.$store.commit('inventory/setError', 'Bill No, Bill Date, Vendor are required.')
        return
      }
      if (!this.lines.length) {
        this.$store.commit('inventory/setError', 'Add at least one line.')
        return
      }

      const cleanLines = []
      for (const ln of this.lines) {
        const sku = (ln.materialSku || '').trim()
        const unit = (ln.unit || '').trim()
        const qty = this.n(ln.qty)
        const rpu = this.n(ln.ratePerUnit)
        if (!sku || !unit || qty === null || rpu === null) {
          this.$store.commit('inventory/setError', 'Each line needs Material, Unit, Qty, Rate/Unit.')
          return
        }
        if (qty <= 0 || rpu <= 0) {
          this.$store.commit('inventory/setError', 'Qty and Rate/Unit must be positive.')
          return
        }
        cleanLines.push({ materialSku: sku, unit, qty, ratePerUnit: rpu })
      }

      const payload = {
        billNo: this.billNo.trim(),
        billDate: this.billDate,
        vendorId: Number(this.vendorId),
        inspectorId: this.inspectorId ? Number(this.inspectorId) : null,
        quality: this.quality,
        note: (this.note || '').trim() || null,
        lines: cleanLines
      }

      const ok = await this.$store.dispatch('inventory/createIncoming', payload)
      if (ok) {
        this.ok = 'Incoming saved.'
        this.billNo = ''
        this.billDate = ''
        this.vendorId = ''
        this.inspectorId = ''
        this.quality = 'good'
        this.note = ''
        this.lines = []
      }
    }
  },
  async mounted () {
    await this.$store.dispatch('inventory/loadMasters')
    if (this.lines.length === 0) this.addLine()
  }
}
</script>

<style scoped>
.row{ display:flex; align-items:center; gap:10px; margin-bottom:10px; }
.grow{ flex:1; }
.grid{ display:grid; grid-template-columns: 1fr 1fr 1fr; gap:12px; margin-top:10px; }
.lbl{ font-weight:700; font-size:12px; color:#6b7280; margin-bottom:6px; }
.span2{ grid-column: span 2; }
.tableWrap{ overflow:auto; margin-top:10px; }
.table{ width:100%; border-collapse:collapse; font-size:14px; }
.table th, .table td{ border-top:1px solid #f0f0f0; padding:10px 8px; }
.muted{ color:#6b7280; text-align:center; padding:16px; }
.err{ color:#b91c1c; margin:8px 0; }
.ok{ color:#15803d; margin:8px 0; font-weight:700; }
.x{ border:1px solid #e5e7eb; background:#fff; border-radius:10px; padding:6px 10px; cursor:pointer; }
@media (max-width: 980px){ .grid{ grid-template-columns:1fr 1fr; } .span2{ grid-column: span 2; } }
@media (max-width: 640px){ .grid{ grid-template-columns:1fr; } .span2{ grid-column: span 1; } }
</style>
